import { numberUtil } from '@xtnd/web/shared/utils';
import { SummaryCardsSkeleton, SummaryCardsGrid, type SummaryCardProps } from '../../../components';
import { useTrustSummary } from '../../../queries/trustReconciliationQuery';
import { ErrorCard } from '@xtnd/web/shared/components';

export function TrustReconciliationSummary() {
  const { data, isLoading, error, refetch } = useTrustSummary();
  if (isLoading) {
    return (
      <SummaryCardsSkeleton
        count={3}
        columns={{ mobile: 1, tablet: 2, desktop: 3 }}
        gap="lg"
        useSkeletonComponent={true}
      />
    );
  }

  if (error) {
    return (
      <ErrorCard
        title="Error Loading Trust Reconciliation Summary"
        message={error instanceof Error ? error.message : 'An unexpected error occurred'}
        onRetry={() => refetch()}
      />
    );
  }

  if (!data) {
    return null;
  }

  const summaryCards: Omit<SummaryCardProps, 'className'>[] = [
    {
      title: 'Total Transactions',
      value: numberUtil.formatNumber(data.totalTransactions),
      subtitle: `${numberUtil.formatNumber(data.totalClioTransactions || 0)} from Clio, ${numberUtil.formatNumber(data.totalQboBankTransactions || 0)} from QBO Bank, ${numberUtil.formatNumber(data.totalQboLiabilityTransactions || 0)} from QBO Liability`,
      icon: 'HiClipboardList',
      color: 'blue',
      variant: 'outlined',
      padding: 'none',
      valueSize: 'lg',
      cardBodyClassName: 'p-4',
      tooltip:
        'Total number of reconciliation records. Each record represents either a matched triplet (Clio + QBO Bank + QBO Liability) or a transaction missing in one or more systems. The Clio, QBO Bank, and QBO Liability counts show how many transactions from each source are included in these records.',
    },
    {
      title: 'Match Success Rate',
      value: numberUtil.formatPercentage(data.matchSuccessRate, 1, false),
      subtitle: `${numberUtil.formatNumber(data.fullyMatched)} of ${numberUtil.formatNumber(data.totalTransactions)} fully matched`,
      icon: 'HiCheckCircle',
      color: 'green',
      variant: 'outlined',
      padding: 'none',
      valueSize: 'lg',
      cardBodyClassName: 'p-4',
      tooltip:
        'Percentage of transactions that are fully matched across all three sources: Clio, QBO Bank, and QBO Liability.',
    },
    {
      title: 'Missing in QBO',
      value: numberUtil.formatNumber(
        (data.missingInQboBank || 0) +
          (data.missingInQboLiability || 0) +
          (data.missingInBothQbo || 0)
      ),
      subtitle: `${numberUtil.formatNumber(data.missingInQboBank || 0)} Bank only, ${numberUtil.formatNumber(data.missingInQboLiability || 0)} Liability only, ${numberUtil.formatNumber(data.missingInBothQbo || 0)} Both`,
      icon: 'HiExclamationCircle',
      color: 'yellow',
      variant: 'outlined',
      padding: 'none',
      valueSize: 'lg',
      cardBodyClassName: 'p-4',
      tooltip:
        'Transactions that exist in Clio but are missing in QuickBooks. This includes transactions missing in QBO Bank account only, QBO Liability account only, or both accounts.',
    },
    {
      title: 'Missing in Clio',
      value: numberUtil.formatNumber(data.missingInClio || 0),
      subtitle: '',
      icon: 'HiExclamationCircle',
      color: 'purple',
      variant: 'outlined',
      padding: 'none',
      valueSize: 'lg',
      cardBodyClassName: 'p-4',
      tooltip:
        'Transactions that exist in QuickBooks (either in the Bank or Liability account) but are missing in Clio. These transactions need to be reviewed and either added to Clio or removed from QuickBooks.',
    },
  ];

  const perAccountTotals = (data.perAccountTotals ?? []) as unknown as Array<{
    source: 'clio' | 'qbo';
    accountId: string;
    accountName: string;
    amount: number;
  }>;

  return (
    <div className="space-y-6">
      {/* Main Summary Cards */}
      <SummaryCardsGrid
        cards={summaryCards.map(card => ({
          ...card,
          className: '',
        }))}
        columns={{ mobile: 1, tablet: 2, desktop: 4 }}
        gap="lg"
      />

      {/* By trust account: one card per source (Clio / QBO) + 1 Liability Account */}
      {perAccountTotals.length > 0 && (
        <div className="space-y-3">
          <SummaryCardsGrid
            cards={[
              ...perAccountTotals.map(item => {
                const isClio = item.source === 'clio';
                return {
                  title: item.accountName,
                  value: numberUtil.formatCurrency(item.amount ?? 0),
                  subtitle: isClio ? 'Clio Trust' : 'QBO Trust (Bank)',
                  icon: isClio ? 'HiDatabase' : 'HiCash',
                  color: isClio ? 'blue' : 'green',
                  variant: 'outlined' as const,
                  padding: 'none' as const,
                  valueSize: 'md' as const,
                  cardBodyClassName: 'p-4',
                };
              }),
              {
                title: 'Liability Account',
                value: numberUtil.formatCurrency(data.totalQboLiabilityAmount ?? 0),
                subtitle: 'QBO Trust Liability',
                icon: 'HiDocumentText' as const,
                color: 'purple' as const,
                variant: 'outlined' as const,
                padding: 'none' as const,
                valueSize: 'md' as const,
                cardBodyClassName: 'p-4',
              },
            ]}
            columns={{ mobile: 1, tablet: 2, desktop: 4 }}
            gap="lg"
          />
        </div>
      )}
    </div>
  );
}
